import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { USE_CASES } from "@/lib/site";
import { fadeUp } from "@/lib/motion";

/**
 * UseCases
 * Dealer-scenario grid. Each card maps a specific dealership pain
 * pattern to a LotWise service recommendation. Prevents the "does
 * this apply to my dealership?" question.
 */
export default function UseCases() {
  return (
    <section
      data-testid="use-cases"
      className="relative bg-[#F4F5F7] text-black"
    >
      <div className="container-lw section-lw">
        <div className="mb-14 grid gap-6 md:grid-cols-[1fr_auto] md:items-end md:mb-16">
          <div>
            <div className="eyebrow text-[#4F0DFF]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#4F0DFF]" />
              Dealership scenarios
            </div>
            <h2 className="mt-5 h-display text-4xl md:text-5xl lg:text-6xl">
              Every dealership looks <br />
              <span className="text-gradient-lw">a little different.</span>
            </h2>
          </div>
          <p className="max-w-md text-[15px] leading-relaxed text-[#4A5359]">
            LotWise fits multiple dealership operating shapes. Find your
            pattern below and see the recommended path in.
          </p>
        </div>

        <ol className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {USE_CASES.map((c, i) => (
            <motion.li
              key={c.slug}
              data-testid={`use-case-${c.slug}`}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              custom={i}
              className="group relative flex flex-col overflow-hidden rounded-[6px] border border-black/10 bg-white p-6 transition hover:border-black/25 hover:shadow-[0_20px_60px_-30px_rgba(0,0,0,0.25)]"
            >
              <div className="flex items-baseline justify-between">
                <span className="font-display text-4xl font-bold tracking-tight text-black/10">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="rounded-full border border-[#4F0DFF]/25 bg-[#4F0DFF]/5 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#4F0DFF]">
                  {c.fit}
                </span>
              </div>

              <h3 className="mt-5 font-display text-xl font-semibold tracking-tight md:text-2xl">
                {c.who}
              </h3>

              <div className="mt-5 border-l-2 border-black/10 pl-4 text-sm text-[#4A5359]">
                <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-black/40">
                  Common pattern
                </div>
                <p className="mt-1.5 leading-relaxed">{c.pain}</p>
              </div>

              <div className="mt-5 flex-1">
                <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-black/40">
                  LotWise recommendation
                </div>
                <p className="mt-1.5 text-[15px] leading-relaxed text-[#2A2F33]">
                  {c.recommend}
                </p>
              </div>

              <Link
                to="/request-a-visit"
                data-testid={`use-case-cta-${c.slug}`}
                className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-black transition group-hover:text-[#4F0DFF]"
              >
                See the Workflow
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </motion.li>
          ))}
        </ol>
      </div>
    </section>
  );
}
